import { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Delete as DeleteIcon,
  Edit,
  Visibility,
} from "@mui/icons-material";
import dayjs from "dayjs";
import UserContext from "../Contexts/UserContext";
import {
  API_GetCandidateIdFromEmail,
  API_GetCandidateJobs,
  API_GetAllLeaveRequests,
  API_GetCompanyIDByJob,
  API_GetCompanyInfo,
  API_DeleteLeaveRequest,
} from "../Assets/ApiUrlContainer";
import { default as axios } from "../axiosConfig";
import LeaveRequestDetails from "./LeaveRequestDetails";
import { Pagination } from "./Pagination";

const EmployeeLeaveRequests = () => {
  const currentUser = useContext(UserContext);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("asc");
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("none");
  const [candidateID, setCandidateID] = useState(null);
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("StartDate");
  const [activeTab, setActiveTab] = useState("current");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveRequestToDelete, setLeaveRequestToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const userEmail = currentUser?.user?.Email;

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  /* Fetch all leave requests for the employee and sort */
  const fetchAllLeaveRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_GetAllLeaveRequests, {
        params: { Email: userEmail, Role: currentUser.user.Role },
      });

      const leaveRequestsUnsorted = response.data.LeaveRequests || [];

      const getApprovalStatus = (req) => {
        if (req.ApprovingTgsManagerId) return "Approved by TGS";
        if (req.ApprovingManagerId) return "Approved by Manager";
        return "Pending Approval";
      };

      const getPrimaryField = (obj) => {
        if (sortField === "Company") {
          return (
            jobs.find((j) => j.Id === obj.JobId)?.CompanyName?.toLowerCase() ||
            ""
          );
        }
        if (sortField === "ApprovingManagerId") {
          return getApprovalStatus(obj);
        }
        return dayjs(convertDateForDayJS(obj[sortField]));
      };

      const sorted = leaveRequestsUnsorted.sort((a, b) => {
        const aPrimary = getPrimaryField(a);
        const bPrimary = getPrimaryField(b);

        if (sortField === "Company" || sortField === "ApprovingManagerId") {
          const primaryCompare =
            sortOrder === "asc"
              ? aPrimary.localeCompare(bPrimary)
              : bPrimary.localeCompare(aPrimary);

          // Always fall back to date sorting within group
          if (primaryCompare !== 0) {
            return primaryCompare;
          }

          // Secondary sort (by Start Date)
          return sortOrder === "asc"
            ? dayjs(convertDateForDayJS(a.StartDate)).diff(dayjs(convertDateForDayJS(b.StartDate)))
            : dayjs(convertDateForDayJS(b.StartDate)).diff(dayjs(convertDateForDayJS(a.StartDate)));
        }
      });

      setLeaveRequests(sorted);

    } catch (error) {
      console.error("Error fetching leave requests:", error);
    } finally {
      setLoading(false);
    }
  }, [userEmail, currentUser.user.Role, sortField, sortOrder, jobs]);

  const fetchCandidateInfo = useCallback(async () => {
    try {
      const response = await axios.get(API_GetCandidateIdFromEmail, {
        params: { Email: userEmail },
      });

      const Id = response.data.Id;
      setCandidateID(Id);

      const res = await axios.get(`${API_GetCandidateJobs}/${Id}`);
      const jobsList = res.data;

      // Fetch all company info in parallel
      const enrichedJobs = await Promise.all(
        jobsList.map(async (job) => {
          try {
            const companyIdRes = await axios.get(`${API_GetCompanyIDByJob}/${job.Id}`);
            const companyId = companyIdRes.data.CompanyId;
            const companyInfoRes = await axios.get(`${API_GetCompanyInfo}/${companyId}`);

            return {
              ...job,
              CompanyName: companyInfoRes.data.Name || "Unknown Company",
            };

          } catch (error) {
            console.error(`Error getting company for job ${job.Id}`, error);
            return { ...job, CompanyName: "Unknown Company" };
          }
        })
      );

      setJobs(enrichedJobs);
      if (enrichedJobs.length > 0) {
        setSelectedJob(enrichedJobs[0].Id);
      }
    } catch (error) {
      console.error("Error fetching candidate info:", error);
    }
  }, [userEmail]);

  // Fetch candidate info once when component mounts
  useEffect(() => {
    if (userEmail) {
      fetchCandidateInfo();
    }
  }, [userEmail, fetchCandidateInfo]);

  useEffect(() => {
    if (userEmail && jobs.length > 0) {
      fetchAllLeaveRequests();
    }
  }, [
    userEmail,
    fetchAllLeaveRequests,
    currentUser.user.Role,
    sortOrder,
    sortField,
    jobs,
  ]);

  const filteredLeaveRequests =
    activeTab === "archived"
      ? leaveRequests.filter((lr) => lr.Status === "archived")
      : leaveRequests.filter((lr) => lr.Status !== "archived");

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this draft?")) return;

    try {
      await axios.delete(`${API_DeleteLeaveRequest}/${leaveRequestToDelete}`);
      await fetchAllLeaveRequests();
      setSnackbarMessage("Leave request deleted successfully.");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting leave request:", error);
    }
  };

  return (
    <div className="">
      <div className="tasksContainer">
        {selectedLeaveRequest ? (
          <LeaveRequestDetails
            leaveRequest={selectedLeaveRequest}
            candidateId={candidateID}
            managerId={null}
            jobId={selectedLeaveRequest.JobId || selectedJob}
            jobName={
              jobs.find(
                (job) => job.Id === (selectedLeaveRequest.JobId || selectedJob)
              )?.Name || "Unknown Job"
            }
            onBack={() => {
              setSelectedLeaveRequest(null);
              fetchAllLeaveRequests();
            }}
            isTGS={false}
          />
        ) : (
          <>
            {/* Sort + Create New Leave Request */}
            <div className="tasksHeader">
              {/* Tabs for Current vs Archived */}
              <div className="leaveRequestFilter"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <h3 style={{ marginRight: "20px" }}>Leave Requests</h3>
                <Button
                  variant={activeTab === "current" ? "contained" : "outlined"}
                  onClick={() => setActiveTab("current")}
                  sx={{
                    mr: 1,
                    backgroundColor:
                      activeTab === "current" ? "#673ab7" : "white",
                    color: activeTab === "current" ? "white" : "#673ab7",
                    borderColor: "#673ab7",
                  }}
                >
                  Current
                </Button>
                <Button
                  variant={activeTab === "archived" ? "contained" : "outlined"}
                  onClick={() => setActiveTab("archived")}
                  sx={{
                    backgroundColor:
                      activeTab === "archived" ? "#673ab7" : "white",
                    color: activeTab === "archived" ? "white" : "#673ab7",
                    borderColor: "#673ab7",
                  }}
                >
                  Archived
                </Button>
              </div>

              {/* Filter Dropdown */}
              <div className="sortingContainer">
                <label>Sort By:</label>
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2 }}
                >
                  <MenuItem value="StartDate">Start Date</MenuItem>
                  <MenuItem value="SubmittedDate">Submission Date</MenuItem>
                  <MenuItem value="Company">Company</MenuItem>
                </Select>
                <IconButton
                  onClick={() =>
                    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                  }
                  size="small"
                  sx={{
                    ml: 1,
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    padding: "4px",
                  }}
                >
                  {sortOrder === "desc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </IconButton>
              </div>

              {/* Create New Leave Request */}
              <div className="newTimesheetContainer">
                <div className="jobSelection">
                  <Select
                    value={selectedJob}
                    onChange={(e) => setSelectedJob(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ m: 1 }}
                  >
                    <MenuItem value="none" disabled>
                      Select a company
                    </MenuItem>
                    {jobs.map((job) => (
                      <MenuItem key={job.Id} value={job.Id}>
                        {job.CompanyName}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    setSelectedLeaveRequest({ Id: null, JobId: selectedJob })
                  }
                  disabled={selectedJob === "none"}
                  sx={{ backgroundColor: "#673ab7" }}
                >
                  Create New Leave Request
                </Button>
              </div>
            </div>

            {loading ? (
              <CircularProgress />
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "20%" }}>
                        <strong>Dates</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Reason</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Company</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Submission Date</strong>
                      </TableCell>
                      <TableCell sx={{ width: "5%" }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {filteredLeaveRequests
                        .slice(start, limit)
                        .map((lr) => (
                          <TableRow
                            key={`${lr.Id}`}
                            sx={{
                              backgroundColor:
                                lr.Status === "rejected"
                                  ? "#FFEBEE"
                                  : "inherit",
                            }}
                          >
                            <TableCell>
                              {dayjs(convertDateForDayJS(lr.StartDate)).format("MMM D, YYYY") +
                                ` - ` +
                                dayjs(convertDateForDayJS(lr.EndDate)).format("MMM D, YYYY")}
                            </TableCell>
                            <TableCell>{lr.TypeOfLeave}</TableCell>
                            <TableCell>
                              {jobs.find((job) => job.Id === lr.JobId)
                                ?.CompanyName || "Unknown Company"}
                            </TableCell>
                            <TableCell>
                              {lr.Status === "approved"
                                ? "Approved"
                                : lr.Status === "submitted"
                                  ? "Pending Approval"
                                  : lr.Status === "rejected"
                                    ? "Rejected"
                                    : lr.Status === "cancelled"
                                      ? "Cancelled"
                                      : lr.Status === "archived"
                                        ? "Archived"
                                        : "Draft"}
                            </TableCell>
                            <TableCell>
                              {dayjs(convertDateForDayJS(lr.SubmittedDate)).format("MMM D, YYYY")}
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <IconButton
                                  variant="outlined"
                                  onClick={() => setSelectedLeaveRequest(lr)}
                                  sx={{
                                    color: "#673ab7",
                                    borderColor: "#673ab7",
                                  }}
                                >
                                  {lr.Status === "rejected" ||
                                    lr.Status === "new" ? (
                                    <Edit />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>

                                {(lr.Status === "rejected" ||
                                  !lr.SubmittedDate) && (
                                    <IconButton
                                      onClick={() => {
                                        setLeaveRequestToDelete(lr.Id);
                                        setDeleteDialogOpen(true);
                                      }}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  </TableBody>
                </Table>
                <Pagination
                  totalNumItems={filteredLeaveRequests.length}
                  onPageChangeNewPosition={setStart}
                  onRowsChangeNewDisplayLimit={setLimit}
                />
              </TableContainer>
            )}
          </>
        )}
      </div>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this leave request? This action cannot
          be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await axios.delete(`${API_DeleteLeaveRequest}/${leaveRequestToDelete}`);
                await fetchAllLeaveRequests();
              } catch (error) {
                console.error("Error deleting leave request:", error);
              }
              setDeleteDialogOpen(false);
              setLeaveRequestToDelete(null);
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmployeeLeaveRequests;
